/**
 * RestrictionBanner — a single data-driven account-restriction banner.
 *
 * The frontend does NOT hard-code restriction types (plan §5.5). Each banner is
 * driven entirely by `{ severity, tk, params }`:
 *   - the banner VARIANT is picked from `severity`
 *       info → sky, warning → amber, critical → red, good → green
 *   - the MESSAGE is `t(tk, params)` — a NEUTRAL, non-leaking i18n key supplied
 *     by the backend (a purely-internal reason is mapped to a neutral key
 *     server-side, never a raw slug).
 *
 * Credit `params` arrive as raw numbers and are locale-formatted here before
 * interpolation so the copy reads "100,000 credits" rather than a bare integer.
 *
 * `AccountRestrictionBanners` is the data-driven container: it reads the current
 * (default) billable account's `restrictions[]` and renders one banner per
 * non-critical restriction. A CRITICAL restriction is not a banner — it gates
 * the account via {@link RestrictedAccountGate}, so it is skipped here.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Info, AlertTriangle, ShieldAlert, CheckCircle2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAccountRestrictions } from "@/hooks/useAccountRestrictions";
import type { AccountRestriction } from "@/services/billingApi";
import { formatCreditsExact } from "@/utils/formatCredits";

type AlertVariant = "info" | "warning" | "destructive" | "success";

/** Maps the backend severity to a banner variant + leading icon. */
const SEVERITY_STYLE: Record<
  AccountRestriction["severity"],
  { variant: AlertVariant; icon: LucideIcon }
> = {
  info: { variant: "info", icon: Info },
  warning: { variant: "warning", icon: AlertTriangle },
  critical: { variant: "destructive", icon: ShieldAlert },
  good: { variant: "success", icon: CheckCircle2 },
};

/**
 * Format the credit `params` for interpolation. Raw numbers become
 * locale-grouped strings; absent fields are omitted so `t()` leaves any unused
 * placeholder untouched.
 */
function formatParams(
  params: AccountRestriction["params"],
): Record<string, string> {
  if (!params) return {};
  const out: Record<string, string> = {};
  if (typeof params.frozenCredits === "number")
    out.frozenCredits = formatCreditsExact(params.frozenCredits);
  if (typeof params.availableCredits === "number")
    out.availableCredits = formatCreditsExact(params.availableCredits);
  if (typeof params.outstandingCredits === "number")
    out.outstandingCredits = formatCreditsExact(params.outstandingCredits);
  return out;
}

export interface RestrictionBannerProps {
  severity: AccountRestriction["severity"];
  /** NEUTRAL i18n key for the message (userFacingTk). */
  tk: string;
  params?: AccountRestriction["params"];
  className?: string;
}

/**
 * Presentational banner for one restriction. Exported standalone so it can be
 * placed anywhere a restriction needs surfacing (not only the account list).
 */
export function RestrictionBanner({
  severity,
  tk,
  params,
  className,
}: RestrictionBannerProps) {
  const { t } = useTranslation();
  const { variant, icon: Icon } = SEVERITY_STYLE[severity];
  const message = t(tk, formatParams(params));

  return (
    <Alert
      variant={variant}
      className={className}
      data-testid="restriction-banner"
      data-severity={severity}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

/**
 * Data-driven container: renders the current account's non-critical
 * restrictions as banners. Renders nothing when there are none. Critical
 * restrictions are handled by the gate, not here.
 */
export function AccountRestrictionBanners({
  className,
}: {
  className?: string;
}) {
  const { restrictions } = useAccountRestrictions();

  const banners = useMemo(
    () => restrictions.filter((r) => r.severity !== "critical"),
    [restrictions],
  );

  if (banners.length === 0) return null;

  return (
    <div
      className={className ?? "flex flex-col gap-2"}
      data-testid="account-restriction-banners"
    >
      {banners.map((r) => (
        <RestrictionBanner
          key={r.reasonSlug}
          severity={r.severity}
          tk={r.userFacingTk}
          params={r.params}
        />
      ))}
    </div>
  );
}
